import { useVehicleValuationCostAdditionPutMutation } from "@/common/api/vehicle-valuation-cost-addition/use-vehicle-valuation-cost-addition-put.mutation";
import { useVehicleValuationCostAdditionSearchQuery } from "@/common/api/vehicle-valuation-cost-addition/use-vehicle-valuation-cost-addition-search.query";
import { handleFormSubmission } from "@/common/helpers/form-submission";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InputTextAlign } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { LocalizedNumberInput } from "@/components/ui/localized-number-input";
import { SaveIcon } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type AdditionFormState,
  buildAdditionFormState,
  getAdditionInputId,
  isNonNegativeNumber,
  parseNonNegativeNumber,
} from "../_helpers/fab-valuation-settings.helpers";
import { AdditionsListSkeleton } from "./fab-valuation-additions-skeleton";

const MANAGED_ADDITION_KEYS = ["DefaultMargin", "VehiclePreparationCosts"] as const;

type ManagedAdditionKey = (typeof MANAGED_ADDITION_KEYS)[number];

const ADDITION_PREFIX_BY_KEY: Record<ManagedAdditionKey, string> = {
  DefaultMargin: "%",
  VehiclePreparationCosts: "€",
};

const DEFAULT_ADDITION_STATE_BY_KEY = {
  DefaultMargin: { key: "DefaultMargin", valueNumber: "0" },
  VehiclePreparationCosts: { key: "VehiclePreparationCosts", valueNumber: "0" },
} satisfies Record<ManagedAdditionKey, AdditionFormState>;

function isManagedAdditionKey(key: string): key is ManagedAdditionKey {
  return MANAGED_ADDITION_KEYS.some((managedKey) => managedKey === key);
}

export function FabValuationAdditions(): ReactElement {
  const { t } = useTranslation("fab");

  const additionsSearchQuery = useVehicleValuationCostAdditionSearchQuery({
    options: {
      body: { key: [...MANAGED_ADDITION_KEYS] },
      parameters: { pageSize: -1 },
    },
  });
  const additionsPutMutation = useVehicleValuationCostAdditionPutMutation();

  const [draftRows, setDraftRows] = useState<Record<string, AdditionFormState>>({});
  const [dirtyRows, setDirtyRows] = useState<Set<string>>(() => new Set());
  const [savingsRow, setSavingsRow] = useState<string | null>(null);

  const additionRows = useMemo(() => {
    const managedRows = buildAdditionFormState(
      (additionsSearchQuery.data?.data ?? []).filter((addition) => isManagedAdditionKey(addition.key)),
    );

    const nextRows = { ...DEFAULT_ADDITION_STATE_BY_KEY };

    for (const key of MANAGED_ADDITION_KEYS) {
      const managedRow = managedRows[key];

      nextRows[key] =
        managedRow && managedRow.valueNumber.trim().length > 0 ? managedRow : DEFAULT_ADDITION_STATE_BY_KEY[key];
    }

    return nextRows;
  }, [additionsSearchQuery.data?.data]);
  const rows = useMemo(() => ({ ...additionRows, ...draftRows }), [additionRows, draftRows]);
  const additionKeys = MANAGED_ADDITION_KEYS;

  function getAdditionDisplayName(key: string): string {
    const translatedKey = t(key, { defaultValue: key });
    if (translatedKey !== key) {
      return translatedKey;
    }

    return t(`CostAdditionName-${key}`, { defaultValue: key });
  }

  function handleInputChange(key: string, value: string): void {
    setDraftRows((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] ?? additionRows[key] ?? { key, valueNumber: "" }),
        valueNumber: value,
      },
    }));

    setDirtyRows((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }

  async function handleSaveRow(key: string): Promise<void> {
    const rowState = rows[key];
    if (!rowState || !isNonNegativeNumber(rowState.valueNumber)) return;

    const settingLabel = getAdditionDisplayName(key);
    setSavingsRow(key);

    await handleFormSubmission(
      () =>
        additionsPutMutation.mutateAsync({
          key,
          valueNumber: parseNonNegativeNumber(rowState.valueNumber),
        }),
      {
        success: t("SettingSaved", { setting: settingLabel }),
        error: t("SettingSaveError", { setting: settingLabel }),
      },
      () => {
        setDirtyRows((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });

        setDraftRows((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      },
    );

    setSavingsRow(null);
  }

  return (
    <section className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("Additions")}</CardTitle>
          <CardDescription>{t("ValuationSettingsDescription")}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {additionsSearchQuery.isLoading && !additionsSearchQuery.data ? (
            <AdditionsListSkeleton />
          ) : additionsSearchQuery.isError && !additionsSearchQuery.data ? (
            <p className="text-error text-sm">{t("CostAdditionsLoadError")}</p>
          ) : (
            <div className="space-y-3">
              {additionKeys.map((name) => {
                const state = rows[name];

                if (!state) {
                  return null;
                }

                const inputId = getAdditionInputId(name);
                const isDirty = dirtyRows.has(name);
                const isSaving = savingsRow === name;
                const showError = state.valueNumber.trim().length > 0 && !isNonNegativeNumber(state.valueNumber);

                return (
                  <div key={name} className="flex flex-row justify-between">
                    <div className="flex min-h-9 items-center">
                      <label className="block text-sm leading-6 font-medium" htmlFor={inputId}>
                        {getAdditionDisplayName(name)}
                      </label>
                    </div>

                    <div className="flex min-h-9 items-center space-x-2 md:justify-end">
                      <div>
                        <LocalizedNumberInput
                          id={inputId}
                          min="0"
                          maxFractionDigits={2}
                          prefix={ADDITION_PREFIX_BY_KEY[name]}
                          textAlign={InputTextAlign.Right}
                          value={state.valueNumber}
                          onValueChange={(value) => handleInputChange(name, value)}
                          error={showError}
                          className="md:w-40"
                        />
                        {showError ? <p className="text-error text-xs">{t("EnterValidNumber")}</p> : null}
                      </div>
                      <Button
                        type="button"
                        variant={isDirty && !isSaving ? ButtonVariant.Filled : ButtonVariant.Outlined}
                        colorVariant={isDirty && !isSaving ? ButtonColorVariant.Primary : ButtonColorVariant.Secondary}
                        size={ButtonSize.Sm}
                        aria-label={t("SaveSetting")}
                        disabled={!isDirty || !isNonNegativeNumber(state.valueNumber) || isSaving}
                        onClick={() => void handleSaveRow(name)}
                      >
                        {isSaving ? (
                          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor />
                        ) : (
                          <SaveIcon />
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
